import PropTypes from 'prop-types';
import React from 'react';
import { animated } from 'react-spring';

import { defaultProps } from '../constants';
import Dots from '../Dots';
import { useSlider } from '../hooks';
import Slide from '../Slide';
import {
  Container as StyledContainer,
  Overlay as StyledOverlay,
  SlideOverlay as StyledSlideOverlay,
  Slider as StyledSlider,
} from './Slider.css';

const Root = animated(StyledContainer);
const AnimatedOverlay = animated(StyledOverlay);
const AnimatedSlider = animated(StyledSlider);

export default function Slider({ slides, slideOverlay, activeDotColor, dotColor, cut }) {
  const [zooming, scale, currentSlide, bind, x, onScale, showNextImageX] = useSlider({
    slides,
  });

  return (
    <Root zooming={zooming}>
      {zooming && (
        <AnimatedOverlay
          style={{
            backgroundColor: scale
              .interpolate({ range: [1, 2, 10], output: [0, 0.7, 0.7] })
              .interpolate((opacity) => `rgba(0, 0, 0, ${opacity})`),
          }}
        />
      )}

      <StyledSlideOverlay inFront={!zooming}>{slideOverlay}</StyledSlideOverlay>

      <AnimatedSlider
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...bind()}
        style={{
          transform: x?.interpolate((slideX) => `translateX(${slideX}px`),
        }}
      >
        {slides.map((slide, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <Slide onScale={onScale} cut={cut} zooming={zooming} key={idx}>
            {slide}
          </Slide>
        ))}
      </AnimatedSlider>

      {slides.length > 1 && (
        <Dots
          totalSlides={slides.length}
          currentSlide={currentSlide}
          centerDots={slides.length < 6 ? slides.length : undefined}
          dotColor={dotColor}
          activeDotColor={activeDotColor}
          onClick={(index) => showNextImageX(index)}
          zooming={zooming}
        />
      )}
    </Root>
  );
}

Slider.propTypes = {
  /** List of slides to render */
  slides: PropTypes.arrayOf(PropTypes.node).isRequired,
  /** Maximum zoom level */
  maxScale: PropTypes.number,
  /** Minimum zoom level */
  minScale: PropTypes.number,
  /** Content to overlay on the slider */
  slideOverlay: PropTypes.node,
  /** Pagination dot color for the active slide */
  activeDotColor: PropTypes.string,
  /** Pagination dot color for all other slides */
  dotColor: PropTypes.string,
  /** Cut sides */
  cut: PropTypes.number,
};

Slider.defaultProps = {
  maxScale: defaultProps.maxScale,
  minScale: defaultProps.minScale,
  slideOverlay: null,
  activeDotColor: defaultProps.activeDotColor,
  dotColor: defaultProps.dotColor,
  cut: defaultProps.cut,
};
